import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminFetch, apiErrorMessage } from '../adminFetch';
import { associer, ligneEnTete, lireFichier, versLignes, type Feuille } from './lecture-fichier';
import './referentiels.css';

/**
 * Référentiels de la suite (14/10/2026) : communes, pays, codes d'activité,
 * formes juridiques, types de voie, correspondances de codes APE.
 *
 * Import d'un fichier (CSV ou Excel) en trois temps : choisir la feuille et
 * associer les colonnes, regarder l'APERÇU (ajouts, modifications, lignes
 * absentes, rejets), puis appliquer. Rien n'est supprimé d'une table que des
 * dossiers référencent : un code absent du nouveau fichier se DÉSACTIVE.
 *
 * Les nomenclatures d'activités coexistent (NAF rév. 2, NAF 2025…), chacune
 * acceptée à la saisie entre deux dates. Le code APE À VENIR de chaque
 * dossier se relève dans SIRENE, puis devient son code à la bascule.
 */

interface Champ {
  cle: string;
  libelle: string;
  requis: boolean;
  indices: string[];
}
interface Definition {
  id: string;
  libelle: string;
  champs: Champ[];
  desactivable: boolean;
  nomenclature: 'aucune' | 'une' | 'deux';
  aide: string;
}
interface Compte {
  total: number;
  actifs: number;
}
interface Nomenclature {
  code: string;
  libelle: string;
  debut: string | null;
  fin: string | null;
  codes: Compte;
  dossiers: number;
}
interface ImportJournal {
  id: string;
  referentiel: string;
  precision: string | null;
  fichier: string | null;
  le: string;
  ajouts: number;
  modifications: number;
  desactivations: number;
  rejets: number;
}
interface Tableau {
  volumes: Record<string, Compte>;
  nomenclatures: Nomenclature[];
  correspondances: { source: string; cible: string; lignes: number }[];
  dossiersAvecCodeSuivant: number;
  imports: ImportJournal[];
}
interface Apercu {
  total: number;
  ajouts: number;
  modifications: number;
  inchanges: number;
  absents: number;
  ignorees: number;
  doublons: number;
  rejets: number;
  exemples: {
    ajouts: Record<string, unknown>[];
    modifications: { cle: string; avant: Record<string, unknown>; apres: Record<string, unknown> }[];
    absents: string[];
    rejets: { ligne: number; motif: string }[];
  };
}
interface EtatReleve {
  enCours: boolean;
  debut: string | null;
  fin: string | null;
  total: number;
  traites: number;
  trouves: number;
  sansCode: number;
  erreurs: number;
}

const json = (corps: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(corps),
});

const dateFr = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

function telecharger(fichier: string, contenu: string) {
  const url = URL.createObjectURL(new Blob([contenu], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fichier;
  a.click();
  URL.revokeObjectURL(url);
}

export function AdminReferentielsPage() {
  const [tableau, setTableau] = useState<Tableau | null>(null);
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const charger = useCallback(async () => {
    const [t, d] = await Promise.all([adminFetch('/admin/referentiels'), adminFetch('/admin/referentiels/definitions')]);
    if (!t.ok) {
      setErreur(await apiErrorMessage(t));
      return;
    }
    setTableau((await t.json()) as Tableau);
    if (d.ok) setDefinitions((await d.json()) as Definition[]);
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  async function exporter(ref: string, nomenclature?: string, cible?: string) {
    const p = new URLSearchParams({ ...(nomenclature ? { nomenclature } : {}), ...(cible ? { cible } : {}) });
    const r = await adminFetch(`/admin/referentiels/${ref}/export?${p.toString()}`);
    if (!r.ok) {
      setErreur(await apiErrorMessage(r));
      return;
    }
    const { fichier, contenu } = (await r.json()) as { fichier: string; contenu: string };
    telecharger(fichier, contenu);
  }

  if (!tableau) return <p>{erreur ?? 'Chargement…'}</p>;

  return (
    <div className="adm-page ref-page">
      <h1>Référentiels</h1>
      <p className="adm-hint">
        Les tables communes à toute la suite : la saisie des dossiers (portail, SEmplyApp, SEmplyCompta, SEmplyImmo) se fait
        par recherche dans ces tables, et une valeur absente est refusée. Mettez-les à jour en important le fichier officiel
        (CSV ou Excel).
      </p>
      {erreur ? (
        <p className="adm-erreur" role="alert">
          {erreur}
        </p>
      ) : null}
      {message ? (
        <p className="ref-ok" role="status">
          {message}
        </p>
      ) : null}

      <SectionNomenclatures
        tableau={tableau}
        onErreur={setErreur}
        onMessage={setMessage}
        onChange={charger}
        onExport={exporter}
      />

      <section className="ref-carte">
        <h2>Autres référentiels</h2>
        <table className="adm-table">
          <thead>
            <tr>
              <th scope="col">Référentiel</th>
              <th scope="col">Lignes actives</th>
              <th scope="col">
                <span className="ref-masque">Export</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(['communes', 'pays', 'formes-juridiques', 'types-voie'] as const).map((ref) => (
              <tr key={ref}>
                <td>{definitions.find((d) => d.id === ref)?.libelle ?? ref}</td>
                <td>
                  {tableau.volumes[ref]?.actifs.toLocaleString('fr-FR')}
                  {tableau.volumes[ref] && tableau.volumes[ref].total !== tableau.volumes[ref].actifs
                    ? ` (${tableau.volumes[ref].total - tableau.volumes[ref].actifs} désactivée(s))`
                    : ''}
                </td>
                <td>
                  <button type="button" className="adm-link" onClick={() => void exporter(ref)}>
                    Exporter (CSV)
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <SectionImport
        definitions={definitions}
        nomenclatures={tableau.nomenclatures}
        onErreur={setErreur}
        onApplique={async (texte) => {
          setMessage(texte);
          await charger();
        }}
      />

      <section className="ref-carte">
        <h2>Derniers imports</h2>
        {tableau.imports.length === 0 ? (
          <p className="adm-hint">Aucun import pour l’instant.</p>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Référentiel</th>
                <th scope="col">Fichier</th>
                <th scope="col">Ajouts</th>
                <th scope="col">Modifications</th>
                <th scope="col">Désactivations</th>
                <th scope="col">Rejets</th>
              </tr>
            </thead>
            <tbody>
              {tableau.imports.map((i) => (
                <tr key={i.id}>
                  <td>{dateFr(i.le)}</td>
                  <td>
                    {definitions.find((d) => d.id === i.referentiel)?.libelle ?? i.referentiel}
                    {i.precision ? ` — ${i.precision}` : ''}
                  </td>
                  <td>{i.fichier ?? '—'}</td>
                  <td>{i.ajouts}</td>
                  <td>{i.modifications}</td>
                  <td>{i.desactivations}</td>
                  <td>{i.rejets}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

// ─── Nomenclatures d'activités, correspondances, codes à venir ──────────────

function SectionNomenclatures({
  tableau,
  onErreur,
  onMessage,
  onChange,
  onExport,
}: {
  tableau: Tableau;
  onErreur: (m: string | null) => void;
  onMessage: (m: string | null) => void;
  onChange: () => Promise<void>;
  onExport: (ref: string, nomenclature?: string, cible?: string) => Promise<void>;
}) {
  const [edition, setEdition] = useState<Record<string, { libelle: string; debut: string; fin: string }>>({});
  const [nouvelle, setNouvelle] = useState({ code: '', libelle: '' });
  const [releve, setReleve] = useState<EtatReleve | null>(null);
  const [bascule, setBascule] = useState<{ nomenclature: string; confirmer: boolean }>({ nomenclature: 'NAF_2025', confirmer: false });
  /** L'avancement du relevé ; suivi toutes les 2 s tant qu'il tourne. */
  const lireReleve = useCallback(async (): Promise<EtatReleve | null> => {
    const r = await adminFetch('/admin/referentiels/codes-ape/releve');
    if (!r.ok) return null;
    const etat = (await r.json()) as EtatReleve;
    setReleve(etat);
    return etat;
  }, []);

  useEffect(() => {
    void lireReleve();
  }, [lireReleve]);

  const enCours = releve?.enCours ?? false;
  useEffect(() => {
    if (!enCours) return undefined;
    const minuterie = setInterval(() => {
      void lireReleve().then((etat) => {
        if (etat && !etat.enCours) void onChange();
      });
    }, 2000);
    return () => clearInterval(minuterie);
  }, [enCours, lireReleve, onChange]);

  const valeur = (n: Nomenclature) => edition[n.code] ?? { libelle: n.libelle, debut: n.debut ?? '', fin: n.fin ?? '' };

  async function enregistrer(code: string, v: { libelle: string; debut: string; fin: string }) {
    onErreur(null);
    const r = await adminFetch(`/admin/referentiels/nomenclatures/${encodeURIComponent(code)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ libelle: v.libelle, debut: v.debut || null, fin: v.fin || null }),
    });
    if (!r.ok) {
      onErreur(await apiErrorMessage(r));
      return;
    }
    setEdition((e) => {
      const { [code]: _retire, ...reste } = e;
      return reste;
    });
    onMessage(`Nomenclature ${code} enregistrée.`);
    await onChange();
  }

  async function demarrerReleve() {
    onErreur(null);
    const r = await adminFetch('/admin/referentiels/codes-ape/releve', { method: 'POST' });
    if (!r.ok) {
      onErreur(await apiErrorMessage(r));
      return;
    }
    setReleve((await r.json()) as EtatReleve);
  }

  async function basculer() {
    onErreur(null);
    const r = await adminFetch('/admin/referentiels/codes-ape/bascule', json({ nomenclature: bascule.nomenclature }));
    setBascule((b) => ({ ...b, confirmer: false }));
    if (!r.ok) {
      onErreur(await apiErrorMessage(r));
      return;
    }
    const { bascules } = (await r.json()) as { bascules: number };
    onMessage(`${bascules} dossier(s) ont désormais leur code ${bascule.nomenclature} comme code APE.`);
    await onChange();
  }

  return (
    <section className="ref-carte">
      <h2>Codes d’activité (APE)</h2>
      <p className="adm-hint">
        Les nomenclatures coexistent : un dossier garde la sienne. À la saisie, seuls les codes d’une nomenclature acceptée ce
        jour-là sont proposés — pendant une transition, les deux ; une date vide veut dire « sans limite ».
      </p>
      <div className="ref-defile">
        <table className="adm-table">
          <thead>
            <tr>
              <th scope="col">Nomenclature</th>
              <th scope="col">Libellé</th>
              <th scope="col">Acceptée du</th>
              <th scope="col">au</th>
              <th scope="col">Codes actifs</th>
              <th scope="col">Dossiers</th>
              <th scope="col">
                <span className="ref-masque">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tableau.nomenclatures.map((n) => {
              const v = valeur(n);
              const modifie = Boolean(edition[n.code]);
              const changer = (cle: 'libelle' | 'debut' | 'fin', x: string) =>
                setEdition((e) => ({ ...e, [n.code]: { ...v, [cle]: x } }));
              return (
                <tr key={n.code}>
                  <td>
                    <code>{n.code}</code>
                  </td>
                  <td>
                    <input aria-label={`Libellé de ${n.code}`} value={v.libelle} onChange={(e) => changer('libelle', e.target.value)} />
                  </td>
                  <td>
                    <input type="date" aria-label={`Acceptée à partir du (${n.code})`} value={v.debut} onChange={(e) => changer('debut', e.target.value)} />
                  </td>
                  <td>
                    <input type="date" aria-label={`Acceptée jusqu’au (${n.code})`} value={v.fin} onChange={(e) => changer('fin', e.target.value)} />
                  </td>
                  <td>
                    {n.codes.actifs.toLocaleString('fr-FR')}
                    {n.codes.total !== n.codes.actifs ? ` / ${n.codes.total}` : ''}
                  </td>
                  <td>{n.dossiers}</td>
                  <td className="ref-actions">
                    {modifie ? (
                      <button type="button" className="btn ref-btn" onClick={() => void enregistrer(n.code, v)}>
                        Enregistrer
                      </button>
                    ) : null}
                    <button type="button" className="adm-link" onClick={() => void onExport('activites', n.code)}>
                      Exporter
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <form
        className="adm-ajout"
        onSubmit={(e) => {
          e.preventDefault();
          void enregistrer(nouvelle.code.trim().toUpperCase(), { libelle: nouvelle.libelle.trim(), debut: '', fin: '' }).then(() =>
            setNouvelle({ code: '', libelle: '' }),
          );
        }}
      >
        <label>
          Nouvelle nomenclature (code)
          <input value={nouvelle.code} placeholder="NAF_2035" pattern="[A-Za-z0-9_]{2,20}" required onChange={(e) => setNouvelle({ ...nouvelle, code: e.target.value })} />
        </label>
        <label>
          Libellé
          <input value={nouvelle.libelle} placeholder="NAF 2035" required onChange={(e) => setNouvelle({ ...nouvelle, libelle: e.target.value })} />
        </label>
        <button type="submit" className="btn">
          Ajouter
        </button>
      </form>

      <h3>Correspondances</h3>
      {tableau.correspondances.length === 0 ? (
        <p className="adm-hint">
          Aucune table de correspondance. Importez celle de l’INSEE (NAF rév. 2 → NAF 2025) : la fiche d’un dossier proposera
          alors les codes qui succèdent au sien.
        </p>
      ) : (
        <ul className="ref-correspondances">
          {tableau.correspondances.map((c) => (
            <li key={`${c.source}-${c.cible}`}>
              {c.source} → {c.cible} : {c.lignes.toLocaleString('fr-FR')} ligne(s){' '}
              <button type="button" className="adm-link" onClick={() => void onExport('correspondances', c.source, c.cible)}>
                Exporter
              </button>
            </li>
          ))}
        </ul>
      )}

      <h3>Code APE à venir de chaque dossier</h3>
      <p className="adm-hint">
        En 2026, SIRENE publie le futur code NAF 2025 de chaque entreprise à côté de son code actuel : c’est celui-là qui
        deviendra son code APE, pas une conversion par la table de correspondance. Le relevé interroge l’annuaire pour chaque
        dossier qui a un SIRET (quelques dossiers par seconde). {tableau.dossiersAvecCodeSuivant} dossier(s) ont déjà un code à venir.
      </p>
      <div className="adm-ajout">
        <button type="button" className="btn" disabled={releve?.enCours} onClick={() => void demarrerReleve()}>
          {releve?.enCours ? 'Relevé en cours…' : 'Relever les codes à venir dans SIRENE'}
        </button>
        {releve && (releve.enCours || releve.fin) ? (
          <p role="status" className="ref-etat">
            {releve.traites} / {releve.total} dossier(s) — {releve.trouves} code(s) trouvé(s), {releve.sansCode} sans code
            {releve.erreurs ? `, ${releve.erreurs} erreur(s)` : ''}
            {releve.fin && !releve.enCours ? ` — terminé le ${dateFr(releve.fin)}` : ''}
          </p>
        ) : null}
      </div>
      <div className="adm-ajout">
        <label>
          Basculer vers
          <select value={bascule.nomenclature} onChange={(e) => setBascule({ nomenclature: e.target.value, confirmer: false })}>
            {tableau.nomenclatures.map((n) => (
              <option key={n.code} value={n.code}>
                {n.libelle}
              </option>
            ))}
          </select>
        </label>
        {bascule.confirmer ? (
          <>
            <button type="button" className="btn ref-btn-danger" onClick={() => void basculer()}>
              Confirmer la bascule
            </button>
            <button type="button" className="adm-link" onClick={() => setBascule((b) => ({ ...b, confirmer: false }))}>
              Annuler
            </button>
          </>
        ) : (
          <button type="button" className="btn" onClick={() => setBascule((b) => ({ ...b, confirmer: true }))}>
            Promouvoir les codes à venir
          </button>
        )}
      </div>
      <p className="adm-hint">
        La bascule fait du code à venir le code APE des dossiers qui en ont un dans cette nomenclature — à faire quand elle
        entre en vigueur (1er janvier 2027 pour la NAF 2025). Les autres gardent le leur ; leur fiche proposera les successeurs.
      </p>
    </section>
  );
}

// ─── Import d'un fichier ─────────────────────────────────────────────────────

function SectionImport({
  definitions,
  nomenclatures,
  onErreur,
  onApplique,
}: {
  definitions: Definition[];
  nomenclatures: Nomenclature[];
  onErreur: (m: string | null) => void;
  onApplique: (message: string) => Promise<void>;
}) {
  const [refId, setRefId] = useState('activites');
  const [nomenclature, setNomenclature] = useState('NAF_2025');
  const [nomenclatureCible, setNomenclatureCible] = useState('NAF_2025');
  const [source, setSource] = useState('NAF_REV2');
  const [fichier, setFichier] = useState<File | null>(null);
  const [feuilles, setFeuilles] = useState<Feuille[]>([]);
  const [feuille, setFeuille] = useState(0);
  const [enTete, setEnTete] = useState(0);
  const [association, setAssociation] = useState<Record<string, number | null>>({});
  const [traiterAbsents, setTraiterAbsents] = useState(false);
  const [apercu, setApercu] = useState<Apercu | null>(null);
  const [travail, setTravail] = useState<'lecture' | 'apercu' | 'application' | null>(null);
  const [confirmer, setConfirmer] = useState(false);

  const def = definitions.find((d) => d.id === refId);
  const lignes = useMemo(() => feuilles[feuille]?.lignes ?? [], [feuilles, feuille]);
  const entetes = lignes[enTete] ?? [];

  function preparer(f: Feuille[], indexFeuille: number, d: Definition | undefined) {
    const l = f[indexFeuille]?.lignes ?? [];
    const e = ligneEnTete(l);
    setEnTete(e);
    setAssociation(d ? associer(l[e] ?? [], d.champs) : {});
    setApercu(null);
    setConfirmer(false);
  }

  async function choisirFichier(f: File | null) {
    onErreur(null);
    setFichier(f);
    setFeuilles([]);
    setApercu(null);
    if (!f) return;
    setTravail('lecture');
    try {
      const lues = await lireFichier(f);
      setFeuilles(lues);
      // La feuille la plus fournie d'abord (les classeurs INSEE ouvrent souvent sur une notice).
      const meilleure = lues.reduce((m, x, i) => (x.lignes.length > (lues[m]?.lignes.length ?? 0) ? i : m), 0);
      setFeuille(meilleure);
      preparer(lues, meilleure, def);
    } catch (e) {
      onErreur(e instanceof Error ? e.message : 'Fichier illisible.');
    } finally {
      setTravail(null);
    }
  }

  const options = () => ({
    ...(def?.nomenclature === 'une' ? { nomenclature } : {}),
    ...(def?.nomenclature === 'deux' ? { nomenclature: source, nomenclatureCible } : {}),
    traiterAbsents,
    fichier: fichier?.name,
  });
  const manquants = def?.champs.filter((c) => c.requis && association[c.cle] == null) ?? [];

  async function envoyer(action: 'apercu' | 'appliquer') {
    if (!def) return;
    onErreur(null);
    setTravail(action === 'apercu' ? 'apercu' : 'application');
    try {
      const r = await adminFetch(`/admin/referentiels/${def.id}/${action}`, json({ lignes: versLignes(lignes, enTete, association), options: options() }));
      if (!r.ok) {
        onErreur(await apiErrorMessage(r));
        return;
      }
      const resultat = (await r.json()) as Apercu;
      if (action === 'apercu') {
        setApercu(resultat);
      } else {
        setApercu(null);
        setConfirmer(false);
        setFichier(null);
        setFeuilles([]);
        await onApplique(
          `Import appliqué : ${resultat.ajouts} ajout(s), ${resultat.modifications} modification(s)` +
            (traiterAbsents ? `, ${resultat.absents} ${def.desactivable ? 'désactivation(s)' : 'suppression(s)'}` : '') +
            (resultat.rejets ? `, ${resultat.rejets} ligne(s) rejetée(s)` : '') +
            '.',
        );
      }
    } finally {
      setTravail(null);
    }
  }

  return (
    <section className="ref-carte">
      <h2>Importer un fichier</h2>
      <div className="adm-ajout">
        <label>
          Référentiel
          <select
            value={refId}
            onChange={(e) => {
              setRefId(e.target.value);
              preparer(feuilles, feuille, definitions.find((d) => d.id === e.target.value));
            }}
          >
            {definitions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.libelle}
              </option>
            ))}
          </select>
        </label>
        {def?.nomenclature === 'une' ? (
          <label>
            Nomenclature des codes
            <select value={nomenclature} onChange={(e) => { setNomenclature(e.target.value); setApercu(null); }}>
              {nomenclatures.map((n) => (
                <option key={n.code} value={n.code}>
                  {n.libelle}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {def?.nomenclature === 'deux' ? (
          <>
            <label>
              De
              <select value={source} onChange={(e) => { setSource(e.target.value); setApercu(null); }}>
                {nomenclatures.map((n) => (
                  <option key={n.code} value={n.code}>
                    {n.libelle}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Vers
              <select value={nomenclatureCible} onChange={(e) => { setNomenclatureCible(e.target.value); setApercu(null); }}>
                {nomenclatures.map((n) => (
                  <option key={n.code} value={n.code}>
                    {n.libelle}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : null}
        <label>
          Fichier (CSV ou Excel .xlsx)
          <input type="file" accept=".csv,.txt,.xlsx" onChange={(e) => void choisirFichier(e.target.files?.[0] ?? null)} />
        </label>
      </div>
      {def ? <p className="adm-hint">{def.aide}</p> : null}
      {travail === 'lecture' ? <p role="status">Lecture du fichier…</p> : null}

      {def && lignes.length > 0 ? (
        <>
          <div className="adm-ajout">
            {feuilles.length > 1 ? (
              <label>
                Feuille
                <select
                  value={feuille}
                  onChange={(e) => {
                    setFeuille(Number(e.target.value));
                    preparer(feuilles, Number(e.target.value), def);
                  }}
                >
                  {feuilles.map((f, i) => (
                    <option key={f.nom} value={i}>
                      {f.nom} ({f.lignes.length} lignes)
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Ligne d’en-tête
              <select
                value={enTete}
                onChange={(e) => {
                  const i = Number(e.target.value);
                  setEnTete(i);
                  setAssociation(associer(lignes[i] ?? [], def.champs));
                  setApercu(null);
                }}
              >
                {lignes.slice(0, 15).map((l, i) => (
                  <option key={i} value={i}>
                    {i + 1} — {l.filter(Boolean).join(' | ').slice(0, 70)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <fieldset className="ref-colonnes">
            <legend>Colonnes du fichier</legend>
            {def.champs.map((c) => (
              <label key={c.cle}>
                {c.libelle}
                {c.requis ? <span aria-hidden="true"> *</span> : null}
                <select
                  value={association[c.cle] ?? ''}
                  required={c.requis}
                  onChange={(e) => {
                    setAssociation((a) => ({ ...a, [c.cle]: e.target.value === '' ? null : Number(e.target.value) }));
                    setApercu(null);
                  }}
                >
                  <option value="">{c.requis ? '— Choisir —' : '— Aucune —'}</option>
                  {entetes.map((t, i) => (
                    <option key={i} value={i}>
                      {t || `Colonne ${i + 1}`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </fieldset>

          <label className="ref-case">
            <input type="checkbox" checked={traiterAbsents} onChange={(e) => { setTraiterAbsents(e.target.checked); setConfirmer(false); }} />
            {def.desactivable
              ? 'Désactiver ce qui n’est pas dans le fichier (refusé à la saisie, gardé pour les dossiers qui le portent)'
              : 'Supprimer ce qui n’est pas dans le fichier'}
          </label>

          <div className="adm-ajout">
            <button type="button" className="btn" disabled={manquants.length > 0 || travail !== null} onClick={() => void envoyer('apercu')}>
              {travail === 'apercu' ? 'Calcul…' : 'Aperçu'}
            </button>
            {manquants.length > 0 ? (
              <p className="adm-alerte">À associer : {manquants.map((c) => c.libelle).join(', ')}.</p>
            ) : null}
          </div>
        </>
      ) : null}

      {apercu && def ? (
        <div className="ref-apercu" aria-live="polite">
          <h3>Aperçu — {apercu.total.toLocaleString('fr-FR')} ligne(s) lue(s)</h3>
          <ul className="ref-chiffres">
            <li>
              <strong>{apercu.ajouts}</strong> ajout(s)
            </li>
            <li>
              <strong>{apercu.modifications}</strong> modification(s)
            </li>
            <li>
              <strong>{apercu.inchanges}</strong> inchangée(s)
            </li>
            <li>
              <strong>{apercu.absents}</strong> absente(s) du fichier{traiterAbsents ? (def.desactivable ? ' → désactivées' : ' → supprimées') : ' (gardées)'}
            </li>
            <li>
              <strong>{apercu.ignorees}</strong> ignorée(s)
            </li>
            <li className={apercu.rejets ? 'ref-alerte' : undefined}>
              <strong>{apercu.rejets}</strong> rejetée(s)
            </li>
            {apercu.doublons ? (
              <li>
                <strong>{apercu.doublons}</strong> doublon(s) — la dernière ligne l’emporte
              </li>
            ) : null}
          </ul>
          {apercu.exemples.modifications.length > 0 ? (
            <details>
              <summary>Exemples de modifications</summary>
              <ul>
                {apercu.exemples.modifications.map((m) => (
                  <li key={m.cle}>
                    <code>{m.cle}</code> :{' '}
                    {Object.keys(m.apres)
                      .map((k) => `${k} « ${String(m.avant[k] ?? '')} » → « ${String(m.apres[k])} »`)
                      .join(' ; ')}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {apercu.exemples.ajouts.length > 0 ? (
            <details>
              <summary>Exemples d’ajouts</summary>
              <ul>
                {apercu.exemples.ajouts.map((a, i) => (
                  <li key={i}>{Object.values(a).filter((x) => typeof x === 'string' && x).slice(0, 3).join(' — ')}</li>
                ))}
              </ul>
            </details>
          ) : null}
          {apercu.exemples.absents.length > 0 ? (
            <details>
              <summary>Exemples d’absents</summary>
              <p>{apercu.exemples.absents.join(', ')}</p>
            </details>
          ) : null}
          {apercu.exemples.rejets.length > 0 ? (
            <details open>
              <summary>Lignes rejetées (non importées)</summary>
              <ul>
                {apercu.exemples.rejets.map((r) => (
                  <li key={r.ligne}>
                    Ligne {r.ligne} : {r.motif}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <div className="adm-ajout">
            {confirmer ? (
              <>
                <button type="button" className="btn ref-btn-danger" disabled={travail !== null} onClick={() => void envoyer('appliquer')}>
                  {travail === 'application' ? 'Application…' : 'Confirmer l’import'}
                </button>
                <button type="button" className="adm-link" onClick={() => setConfirmer(false)}>
                  Annuler
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn"
                disabled={apercu.ajouts + apercu.modifications + (traiterAbsents ? apercu.absents : 0) === 0}
                onClick={() => setConfirmer(true)}
              >
                Appliquer
              </button>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
